import { Direction, Popover as PopoverPrimitive } from 'radix-ui';
import * as React from 'react';

import { cn } from '@/lib/cn';

function Popover({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Root>) {
  return <PopoverPrimitive.Root data-slot="popover" {...props} />;
}

const PopoverTrigger = React.forwardRef<
  React.ComponentRef<typeof PopoverPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Trigger>
>(({ ...props }, ref) => {
  return <PopoverPrimitive.Trigger ref={ref} data-slot="popover-trigger" {...props} />;
});
PopoverTrigger.displayName = 'PopoverTrigger';

const PopoverContent = React.forwardRef<
  React.ComponentRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, align = 'center', sideOffset = 4, dir, onWheel, onTouchMove, ...props }, ref) => {
  // Portals render outside the DOM subtree that carries `dir` — read the
  // ambient DirectionProvider (radix-ui `Direction`, set at the v4 shell root
  // in AppShell) so `rtl:` utilities inside the portal resolve correctly.
  const direction = Direction.useDirection(dir as 'ltr' | 'rtl' | undefined);
  return (
    // forceMount must reach the Portal too — its own Presence unmounts closed
    // content otherwise, defeating keep-mounted consumers (in-flight uploads).
    <PopoverPrimitive.Portal forceMount={props.forceMount}>
      <PopoverPrimitive.Content
        ref={ref}
        data-slot="popover-content"
        align={align}
        sideOffset={sideOffset}
        dir={direction}
        // Modal Dialogs mount react-remove-scroll, whose document-level wheel/
        // touchmove handler preventDefaults events targeting this portal (it
        // sits outside the dialog subtree) — scrollable popover content
        // (Command lists) becomes wheel-dead. Stopping propagation keeps the
        // events off that handler; native scrolling is unaffected.
        onWheel={(event) => {
          onWheel?.(event);
          event.stopPropagation();
        }}
        onTouchMove={(event) => {
          onTouchMove?.(event);
          event.stopPropagation();
        }}
        className={cn(
          'z-50 w-72 origin-(--radix-popover-content-transform-origin) rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-hidden data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
          className
        )}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
});
PopoverContent.displayName = 'PopoverContent';

const PopoverAnchor = React.forwardRef<
  React.ComponentRef<typeof PopoverPrimitive.Anchor>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Anchor>
>(({ ...props }, ref) => {
  return <PopoverPrimitive.Anchor ref={ref} data-slot="popover-anchor" {...props} />;
});
PopoverAnchor.displayName = 'PopoverAnchor';

const PopoverHeader = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<'div'>>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        data-slot="popover-header"
        className={cn('flex flex-col gap-1 text-sm', className)}
        {...props}
      />
    );
  }
);
PopoverHeader.displayName = 'PopoverHeader';

const PopoverTitle = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<'div'>>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        data-slot="popover-title"
        className={cn('font-medium', className)}
        {...props}
      />
    );
  }
);
PopoverTitle.displayName = 'PopoverTitle';

const PopoverDescription = React.forwardRef<
  HTMLParagraphElement,
  React.ComponentPropsWithoutRef<'p'>
>(({ className, ...props }, ref) => {
  return (
    <p
      ref={ref}
      data-slot="popover-description"
      className={cn('text-muted-foreground', className)}
      {...props}
    />
  );
});
PopoverDescription.displayName = 'PopoverDescription';

export {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
};
